import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import type { QueryClient } from 'react-query'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import CONSTANTS from 'utils/constants'
import type {
  BulkUpdatePayByPayload,
  ReconciliationFilters,
  ReconciliationListApiResponse,
  UpdateReconciliationPayload
} from '../utils/reconciliationTypes'
import { PAY_REPORT_QUERY_KEY } from './usePayReport'
import { PAY_REPORT_DRAWER_QUERY_KEY } from './usePayReportDrawer'

export const RECONCILIATION_QUERY_KEY = 'reconciliation'

function invalidatePayReportQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries([PAY_REPORT_QUERY_KEY])
  queryClient.invalidateQueries([PAY_REPORT_DRAWER_QUERY_KEY])
}

function decodeQueryStringForLog(qs: string): string {
  if (!qs) {
    return ''
  }
  return qs
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=')
      if (eq === -1) {
        return decodeURIComponent(pair)
      }
      return `${decodeURIComponent(pair.slice(0, eq))}=${decodeURIComponent(pair.slice(eq + 1))}`
    })
    .join('&')
}

function logReconciliationGetRequestUrl(qs: string) {
  const urlPath = `reconciliation?${qs}`
  const baseUrl = CONSTANTS.VITE_APP_ROTA_API
  const fullUrl = baseUrl
    ? `${baseUrl}${urlPath.startsWith('/') ? '' : '/'}${urlPath}`
    : urlPath
  const q = fullUrl.indexOf('?')
  const unencoded =
    q === -1
      ? fullUrl
      : `${fullUrl.slice(0, q + 1)}${decodeQueryStringForLog(fullUrl.slice(q + 1))}`
  console.log('[reconciliation GET]', unencoded)
}

export const buildReconciliationQueryString = (
  filters: ReconciliationFilters
): string => {
  const params = new URLSearchParams()
  params.set('startTime', filters.startTime)
  params.set('endTime', filters.endTime)
  if (filters.status) {
    params.set('status', filters.status)
  }
  filters.teamMemberIds?.forEach((id, i) => {
    params.set(`teamMemberIds[${i}]`, id)
  })
  filters.serviceUserIds?.forEach((id, i) => {
    params.set(`serviceUserIds[${i}]`, id)
  })
  filters.eventTypeIds?.forEach((id, i) => {
    params.set(`eventTypeIds[${i}]`, id)
  })
  filters.durationDiscrepancy?.forEach((d) => {
    params.append('durationDiscrepancy[]', d)
  })
  if (filters.page !== undefined && filters.pageSize !== undefined) {
    params.set('pagination[page]', String(Math.max(1, filters.page)))
    params.set('pagination[limit]', String(filters.pageSize))
  }
  if (filters.sortField !== undefined && filters.sortDir !== undefined) {
    params.set(`sort[${filters.sortField}]`, String(filters.sortDir))
  } else {
    params.set('sort[startTime]', '-1')
  }
  return params.toString()
}

export const useGetReconciliation = (filters: ReconciliationFilters) => {
  const qs = buildReconciliationQueryString(filters)
  return useQuery<ReconciliationListApiResponse>(
    [RECONCILIATION_QUERY_KEY, filters],
    () => {
      logReconciliationGetRequestUrl(qs)
      return getJSON({
        url: `reconciliation?${qs}`,
        isRota: true
      })
    },
    {
      staleTime: 60 * 1000,
      keepPreviousData: true
    }
  )
}

export const useUpdateReconciliation = (onSuccess?: () => void) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    ({
      rotaInstanceId,
      payload
    }: {
      rotaInstanceId: string
      payload: UpdateReconciliationPayload
    }) =>
      postJSON({
        url: `reconciliation/${rotaInstanceId}`,
        method: 'PUT',
        isRota: true,
        json: payload
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        invalidatePayReportQueries(queryClient)
        showToast({
          type: 'success',
          title: t('RECONCILIATION_TOAST_UPDATE_SUCCESS')
        })
        onSuccess?.()
      },
      onError: (error: any) =>
        showToast({
          type: 'error',
          title: error?.message || t('RECONCILIATION_TOAST_UPDATE_ERROR')
        })
    }
  )
}

export const useBulkUpdatePayBy = (onSuccess?: () => void) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    (payload: BulkUpdatePayByPayload) =>
      postJSON({
        url: 'reconciliation/bulk',
        method: 'PUT',
        isRota: true,
        json: payload
      }),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        invalidatePayReportQueries(queryClient)
        const count = variables.paymentIds.length
        showToast({
          type: 'success',
          title:
            count > 1
              ? t('RECONCILIATION_TOAST_BULK_SUCCESS_MANY', { count })
              : t('RECONCILIATION_TOAST_BULK_SUCCESS_ONE')
        })
        onSuccess?.()
      },
      onError: (error: any) =>
        showToast({
          type: 'error',
          title: error?.message || t('RECONCILIATION_TOAST_BULK_ERROR')
        })
    }
  )
}
